import { InputHTMLElement } from "components/input/baseHTMLElementInput";
import template from "components/input/baseInputInput.hbs";
import { Tooltip } from "components/components/tooltip/tooltip";
import IMask from "imask";
import i18n from "components/i18n/i18n";
import numbro from "numbro";
import sislogUserGlobals from "components/UserGlobals";

export class NumberInputElement extends InputHTMLElement {

  private static separators: string;

  private mask: IMask.InputMask<IMask.MaskedNumberOptions>;
  private min: number = null;
  private max: number = null;
  private decimals: number = 0;

  constructor() {
    super();
    NumberInputElement.separators = NumberInputElement.separators || i18n.instance.t("locale.separators");

    const decimals = this.getAttribute("decimals");
    if (this.isNumeric(decimals)) {
      this.decimals = parseInt(decimals, 10);
    }
    const max = this.getAttribute("max");
    if (this.isNumeric(max)) {
      this.max = parseFloat(max);
    }
    const min = this.getAttribute("min");
    if (this.isNumeric(min)) {
      this.min = parseFloat(min);
    }

    const optTemplate = {
      "prefixId": "ni",
      "id": this.getAttribute("_id"),
      "className": "c-number-input__input o-form-component__input o-form-component__input-group__input",
      "style": this.getAttribute("_style"),
      "step": this.getAttribute("step") ?? "any",
      "unit": this.getAttribute("unit"),
      "disableFocusKeyboard": (this.getAttribute("disableFocusKeyboard") === 'true'),
    } as any;
    if (this.max != null) {
      optTemplate.max = this.max;
    }
    if (this.min != null) {
      optTemplate.min = this.min;
    }

    this.innerHTML = template(optTemplate);

    this.input = this.querySelector("input");
    this.mask = this.initMask();

    this.bindEvents();
    Tooltip.bindElement(this);
  }

  private initMask(): IMask.InputMask<IMask.MaskedNumberOptions> {

    const scale = this.decimals;
    const signed = (this.min == null && this.max == null) || (this.min == null && this.max != null) || this.min < 0 || this.max < 0;
    const padFractionalZeros = (this.decimals > 0);

    const maskOptions = {
      mask: Number,
      scale, // digits after point, 0 for integers
      padFractionalZeros, // if true, then pads zeros at end to the length of scale
      signed, // allow / disallow negative
      //normalizeZeros: true,  // appends or removes zeros at ends
      thousandsSeparator: NumberInputElement.separators[0],
      radix: NumberInputElement.separators[1],
    } as any;

    if (this.min != null) {
      maskOptions.min = this.min;
    }
    if (this.max != null) {
      maskOptions.max = this.max;
    }
    return IMask(this.input, maskOptions);
  }

  private bindEvents(): void {

    this.genericBindEvents();

    this.addEventFocusin_selectContent();
  }

  public isEqualToInitialValue(value: any): boolean {
    let isEqual: boolean = true;
    const initialValue = this.initialValue.value as number;

    if (value != null) {
      value = this.numbroUnformat(value);
    }

    isEqual = (initialValue === value);

    return isEqual;
  }

  private doFormat(v: number, withThousandSeparated: boolean = true): string {
    if (v != null) {

      let options = {
        mantissa: this.decimals,
        //optionalMantissa: true,
      } as any;

      if (withThousandSeparated) {
        options.thousandSeparated = NumberInputElement.separators[0] ? true : false;
      }

      return this.numbroFormat(v, options);
    }

    return null;
  }

  get data(): number {
    let v = this.input.value;
    if (v === "") {
      return null;
    } else {
      return this.getNumberValue(v);
    }
  }

  set data(v: number) {
    this.input.value = this.doFormat(v, false) ?? null;
    this.initialValue.value = this.data;
    this.mask?.updateValue();
    // Si se trabaja con el updateValue de la mascara, pierde el formato. Por el locale
    // Hay q hacer el typedValue
    this.maskTypedValue(v);
  }

  private getNumberValue(v: string): number {
    let val = this.numbroUnformat(v);
    // It is not necessary to validate the maximum value because the possibility of typing it is blocked
    val = this.reassignIMaskMinValue(val);
    return val;
  }

  private maskTypedValue(v: number) {
    // The mask down't konw how to handle a decimal and thousands signals
    this.mask.typedValue = ((v == null) ? null : (this.decimals > 0) ? v : Math.trunc(v));
  }

  // This function is associated with the logic of Imask min
  private reassignIMaskMinValue(val: number): number {
    let _v = val;
    if (this.min != null && val < this.min) {
      _v = this.min;
    }
    return _v;
  }

  private numbroFormat(v: any, options?: string | numbro.Format): string {
    numbro.setLanguage(sislogUserGlobals.language);
    return numbro(v).format(options);
  }

  private numbroUnformat(v: string): number {
    numbro.setLanguage(sislogUserGlobals.language);
    return numbro.unformat(v);
  }

}

export function initNumberInputWebComponent() {
  window.customElements.define("s6-number-input", NumberInputElement);
}
